import React, { useCallback, useState, forwardRef, memo } from 'react';
import { FormattedMessage } from 'react-intl';
import type { Moment } from 'moment';

import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import type { DateRange } from '@mui/x-date-pickers-pro';
import Button from '@mui/material/Button';

import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import elements from '@/components/elements';

interface ButtonFieldProps {
  setOpen?: (update: (prev: boolean) => boolean) => void;
  open?: boolean;
  id?: string;
  InputProps?: { ref?: React.Ref<HTMLButtonElement> };
  inputProps?: { 'aria-label'?: string };
}

interface DateRangeFilterProps {
  range?: DateRange<Moment>;
  onChange: (range: [Moment, Moment]) => void;
}

const ButtonField = forwardRef<HTMLButtonElement, ButtonFieldProps>((props, _ref) => {
  const { setOpen, open, id, InputProps: { ref: buttonRef } = {}, inputProps: { 'aria-label': ariaLabel } = {} } = props;

  return (
    <Button
      ref={buttonRef}
      id={id}
      onClick={() => setOpen?.((prev) => !prev)}
      variant="text"
      size="small"
      startIcon={<EventOutlinedIcon />}
      endIcon={open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
      aria-label={ariaLabel}
      data-element={elements.history.calendar}
    >
      <FormattedMessage id="eventsHistory.panel.filter.date" defaultMessage="Date" />
    </Button>
  );
});
// https://github.com/mui/mui-x/issues/10469
// add this line to signal to the range picker to expose the props for a single input range picker
(ButtonField as typeof ButtonField & { fieldType: string }).fieldType = 'single-input';

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ range = [null, null], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = useCallback(
    ([start, end]: DateRange<Moment>) => {
      if (typeof onChange === 'function') {
        if (start && end) {
          onChange([start, end]);
        }
      }
    },
    [onChange],
  );

  const handleOpen = useCallback(() => setIsOpen(true), []);

  const handleClose = useCallback(() => setIsOpen(false), []);

  return (
    <DateRangePicker
      slots={{ field: ButtonField }}
      // `field` is typed against the multi-input field props; this picker uses the
      // single-input variant declared by ButtonField.fieldType above, whose props
      // MUI's types do not model (mui/mui-x#10469).
      slotProps={{ field: { setOpen: setIsOpen, open: isOpen } as never, popper: { placement: 'auto' } }}
      disableFuture
      value={range}
      onChange={handleChange}
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
    />
  );
};

export default memo(DateRangeFilter);
